import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { ApiRequest } from '@/utils/axios';
import { IUser } from '@/utils/types/User';
import { toFullSteamAvatar } from '@/utils/avatar';

interface Props {
    value: number | null;
    onChange: (userId: number | null) => void;
    label?: string;
}

// /admin/users/list использует getRawMany → колонка balanceUsd может прийти
// строкой (особенно когда тип real, у некоторых драйверных конфигов pg
// сериализует как string). Безопасно приводим к числу.
function formatBalance(raw: unknown): string {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
}

/**
 * Дропдаун для выбора бота (isFake=true) в админских модалках —
 * чтобы ставка отправлялась от имени бота, а не от админа.
 * Если value=null — бэк сам подберёт случайного бота (findRandomFake).
 */
const AdminBotPicker = ({ value, onChange, label = 'Bot' }: Props) => {
    const [bots, setBots] = useState<IUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await ApiRequest.post('/admin/users/list', {
                    filter: 'BOTS',
                    page: 1,
                    limit: 500,
                });
                if (!cancelled && data?.response?.items) {
                    setBots(data.response.items);
                }
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return bots;
        return bots.filter(
            (b) =>
                b.name?.toLowerCase().includes(q) || String(b.id).includes(q)
        );
    }, [bots, search]);

    const selectedBot = useMemo(
        () => bots.find((b) => b.id === value),
        [bots, value]
    );

    return (
        <div className="flex flex-col gap-2 min-w-0">
            <div className="text-[11.5px] uppercase tracking-[0.08em] text-text_3 font-semibold">
                {label}
            </div>
            <div ref={wrapRef} className="relative">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className={
                        'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-fl border text-left transition-colors ' +
                        (open
                            ? 'border-stroke_strong bg-layer_3'
                            : 'border-stroke bg-layer_2 hover:border-stroke_strong')
                    }
                >
                    <div className="flex items-center gap-2.5 min-w-0">
                        {selectedBot?.avatar ? (
                            <Image
                                src={toFullSteamAvatar(selectedBot.avatar)}
                                alt=""
                                width={28}
                                height={28}
                                className="rounded-full shrink-0"
                                unoptimized
                            />
                        ) : (
                            <div className="w-7 h-7 rounded-full bg-layer_3 shrink-0" />
                        )}
                        <div className="flex flex-col min-w-0">
                            <div className="text-[12.5px] font-bold text-text_main truncate">
                                {selectedBot
                                    ? selectedBot.name
                                    : loading
                                        ? 'Loading bots…'
                                        : 'Random bot (auto)'}
                            </div>
                            {selectedBot && (
                                <div className="text-[10.5px] text-text_3 tabular-nums">
                                    #{selectedBot.id} · balance ${formatBalance(selectedBot.balanceUsd)}
                                </div>
                            )}
                        </div>
                    </div>
                    <svg
                        viewBox="0 0 24 24"
                        className={`w-4 h-4 stroke-text_3 fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round] transition-transform ${open ? 'rotate-180' : ''}`}
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                {open && (
                    <div
                        className="absolute left-0 right-0 top-full mt-1.5 rounded-fl border border-stroke_strong overflow-hidden z-30"
                        style={{
                            background: 'rgba(28,30,38,0.98)',
                            boxShadow: '0 16px 36px rgba(0,0,0,0.55)',
                        }}
                    >
                        <div className="p-2 border-b border-stroke">
                            <input
                                type="text"
                                placeholder="Search by name or id"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full px-3 py-2 rounded-fl bg-layer_2 border border-stroke text-[12.5px] text-text_main placeholder:text-text_4 outline-none focus:border-accent transition-colors"
                                autoFocus
                            />
                        </div>
                        <div className="max-h-72 overflow-y-auto fluent-scrollbar">
                            <button
                                type="button"
                                onClick={() => {
                                    onChange(null);
                                    setOpen(false);
                                }}
                                className={
                                    'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ' +
                                    (value === null
                                        ? 'bg-layer_3'
                                        : 'hover:bg-layer_3')
                                }
                            >
                                <div className="w-7 h-7 rounded-full bg-layer_3 grid place-items-center text-text_3 text-[14px]">?</div>
                                <div className="flex flex-col">
                                    <div className="text-[12.5px] font-bold text-text_main">Random bot (auto)</div>
                                    <div className="text-[10.5px] text-text_3">Backend will pick</div>
                                </div>
                            </button>
                            {loading ? (
                                <div className="px-3 py-4 text-center text-text_3 text-[12px]">Loading…</div>
                            ) : filtered.length === 0 ? (
                                <div className="px-3 py-4 text-center text-text_3 text-[12px]">No bots found</div>
                            ) : (
                                filtered.map((bot) => {
                                    const isSelected = bot.id === value;
                                    return (
                                        <button
                                            key={bot.id}
                                            type="button"
                                            onClick={() => {
                                                onChange(bot.id);
                                                setOpen(false);
                                            }}
                                            className={
                                                'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ' +
                                                (isSelected ? 'bg-layer_3 shadow-[inset_3px_0_0_var(--accent,#4cc2ff)]' : 'hover:bg-layer_3')
                                            }
                                            style={isSelected ? { boxShadow: 'inset 3px 0 0 #4cc2ff' } : undefined}
                                        >
                                            {bot.avatar ? (
                                                <Image
                                                    src={toFullSteamAvatar(bot.avatar)}
                                                    alt=""
                                                    width={28}
                                                    height={28}
                                                    className="rounded-full shrink-0"
                                                    unoptimized
                                                />
                                            ) : (
                                                <div className="w-7 h-7 rounded-full bg-layer_3 shrink-0" />
                                            )}
                                            <div className="flex flex-col min-w-0">
                                                <div className="text-[12.5px] font-bold text-text_main truncate">
                                                    {bot.name}
                                                </div>
                                                <div className="text-[10.5px] text-text_3 tabular-nums">
                                                    #{bot.id} · balance ${formatBalance(bot.balanceUsd)}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminBotPicker;
